/**
 * ViewSettings store — Svelte 5 runes
 *
 * Reactive store mirroring the legacy TL.Model.ViewSettings Backbone model.
 * The store is the runtime source of truth for the UI; the bridge keeps it
 * in sync with the Backbone model (which still owns localStorage persistence).
 */

import {
  DEFAULT_VIEW_SETTINGS,
  type ViewSettings,
  type ViewSettingsPatch,
  type WorkordersFilter,
  type SitesFilter,
  type ResourceIcons,
  type MapOverlays,
} from '../types/view-settings';

type ChangeListener = (snapshot: ViewSettings) => void;

/**
 * Deep-merge `source` into `target` in place. Plain objects are merged
 * recursively; arrays and primitives are replaced. Mutates `target`.
 */
function deepMerge<T>(target: T, source: unknown): void {
  if (!isPlainObject(source) || !isPlainObject(target)) return;
  for (const key of Object.keys(source)) {
    const srcVal = (source as Record<string, unknown>)[key];
    const tgtVal = (target as Record<string, unknown>)[key];
    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      deepMerge(tgtVal, srcVal);
    } else {
      (target as Record<string, unknown>)[key] =
        isPlainObject(srcVal) || Array.isArray(srcVal)
          ? structuredClone(srcVal)
          : srcVal;
    }
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Returns true iff every key in `group` (excluding `allKey`) is true.
 */
function allChildrenTrue<G extends Record<string, boolean>>(
  group: G,
  allKey: keyof G
): boolean {
  for (const k of Object.keys(group)) {
    if (k === allKey) continue;
    if (!group[k as keyof G]) return false;
  }
  return true;
}

export class ViewSettingsStore {
  #state = $state<ViewSettings>(structuredClone(DEFAULT_VIEW_SETTINGS));
  #listeners = new Set<ChangeListener>();

  /** True iff every individual workorder flag is true. */
  readonly workordersAll: boolean = $derived(
    allChildrenTrue(this.#state.workorders, 'workorders-all')
  );

  /** True iff every individual site flag is true. */
  readonly sitesAll: boolean = $derived(
    allChildrenTrue(this.#state.sites, 'sites-all')
  );

  constructor(initial?: ViewSettingsPatch) {
    if (initial) deepMerge(this.#state, initial);
    this.#applyInvariants();
  }

  /** Get a snapshot of the entire state (deep-cloned, non-reactive). */
  get(): ViewSettings;
  /** Get a snapshot of a single top-level key. */
  get<K extends keyof ViewSettings>(key: K): ViewSettings[K];
  get<K extends keyof ViewSettings>(key?: K): ViewSettings | ViewSettings[K] {
    if (key === undefined) return $state.snapshot(this.#state) as ViewSettings;
    return $state.snapshot(this.#state[key]) as ViewSettings[K];
  }

  /** Replace a single top-level key. Triggers onChange unless silent. */
  set<K extends keyof ViewSettings>(
    key: K,
    value: ViewSettings[K],
    silent = false
  ): void {
    const cloned = isPlainObject(value) || Array.isArray(value)
      ? (structuredClone(value) as ViewSettings[K])
      : value;
    this.#state[key] = cloned;
    this.#applyInvariants();
    if (!silent) this.#notify();
  }

  /** Deep-merge a partial patch. Triggers onChange unless silent. */
  patch(partial: ViewSettingsPatch, silent = false): void {
    deepMerge(this.#state, partial);
    this.#applyInvariants();
    if (!silent) this.#notify();
  }

  /** Replace the entire state with `full`. Triggers onChange unless silent. */
  reset(full: ViewSettings, silent = false): void {
    const cloned = structuredClone(full);
    for (const key of Object.keys(this.#state) as (keyof ViewSettings)[]) {
      if (!(key in cloned)) delete (this.#state as Record<string, unknown>)[key];
    }
    Object.assign(this.#state, cloned);
    this.#applyInvariants();
    if (!silent) this.#notify();
  }

  /**
   * Toggle a workorder filter. Passing `'workorders-all'` toggles every
   * individual flag based on the current "all" state.
   */
  toggleWorkorder(key: keyof WorkordersFilter): void {
    if (key === 'workorders-all') {
      const next = !this.workordersAll;
      for (const k of Object.keys(this.#state.workorders) as (keyof WorkordersFilter)[]) {
        this.#state.workorders[k] = next;
      }
    } else {
      this.#state.workorders[key] = !this.#state.workorders[key];
    }
    this.#applyInvariants();
    this.#notify();
  }

  /**
   * Toggle a site filter. Passing `'sites-all'` toggles every individual
   * flag based on the current "all" state.
   */
  toggleSite(key: keyof SitesFilter): void {
    if (key === 'sites-all') {
      const next = !this.sitesAll;
      for (const k of Object.keys(this.#state.sites) as (keyof SitesFilter)[]) {
        this.#state.sites[k] = next;
      }
    } else {
      this.#state.sites[key] = !this.#state.sites[key];
    }
    this.#applyInvariants();
    this.#notify();
  }

  /** Toggle a single resource-icons flag (positions / events). */
  toggleResourceIcon(key: keyof ResourceIcons): void {
    this.#state.resourceIcons[key] = !this.#state.resourceIcons[key];
    this.#notify();
  }

  /** Toggle a map overlay flag. */
  toggleMapOverlay(key: keyof MapOverlays): void {
    this.#state.map.overlays[key] = !this.#state.map.overlays[key];
    this.#notify();
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function.
   * The callback receives a deep-cloned snapshot of the new state.
   */
  onChange(cb: ChangeListener): () => void {
    this.#listeners.add(cb);
    return () => {
      this.#listeners.delete(cb);
    };
  }

  /**
   * Keep derived-but-materialized fields (`workorders.workorders-all`,
   * `sites.sites-all`) consistent with their individual flags so the
   * serialized state matches what legacy code expects to read.
   */
  #applyInvariants(): void {
    this.#state.workorders['workorders-all'] = allChildrenTrue(
      this.#state.workorders,
      'workorders-all'
    );
    this.#state.sites['sites-all'] = allChildrenTrue(
      this.#state.sites,
      'sites-all'
    );
  }

  #notify(): void {
    const snapshot = $state.snapshot(this.#state) as ViewSettings;
    for (const cb of Array.from(this.#listeners)) {
      try {
        cb(snapshot);
      } catch (e) {
        console.error('[viewSettingsStore] listener error', e);
      }
    }
  }
}

/** Singleton instance shared across the app. */
export const viewSettingsStore = new ViewSettingsStore();

/** Selective re-exports — migration path for future store splitting. */
export const workorderFilters = {
  get: () => viewSettingsStore.get('workorders'),
  toggle: (key: keyof WorkordersFilter) => viewSettingsStore.toggleWorkorder(key),
};

export const siteFilters = {
  get: () => viewSettingsStore.get('sites'),
  toggle: (key: keyof SitesFilter) => viewSettingsStore.toggleSite(key),
};

export const mapSettings = {
  get: () => viewSettingsStore.get('map'),
  toggleOverlay: (key: keyof MapOverlays) =>
    viewSettingsStore.toggleMapOverlay(key),
};
